// =============================================================================
// src/engine/sampleCopedants.ts
// Built-in copedant presets and a CSV import utility.
// =============================================================================

import type { Copedant } from "./types";
import { parseNoteToMidi } from "./pitch";

// =============================================================================
// PRESET: E9 Nashville Standard
// The classic 10-string pedal steel configuration used in country/sacred steel.
//
//  String:  1     2     3     4     5     6     7     8     9    10
//  Open:    F#5   D#5   G#4   E4    B3    G#3   E3    D3    B2   E2
//  (String 1 is the highest-pitched string; 10 is the lowest.)
//
//  Pedals:   A, B, C
//  Levers:   LKL (left knee left), LKR (left knee right),
//            RKL (right knee left), RKR (right knee right)
// =============================================================================

export const E9_NASHVILLE: Copedant = {
  id: "e9-nashville",
  name: "E9 Nashville Standard",

  // Index 0 = string 1 (highest), index 9 = string 10 (lowest)
  openTuning: [
    "F#5",  // string 1
    "D#5",  // string 2
    "G#4",  // string 3
    "E4",   // string 4
    "B3",   // string 5
    "G#3",  // string 6
    "E3",   // string 7
    "D3",   // string 8
    "B2",   // string 9
    "E2",   // string 10
  ],

  controls: [
    // --- Foot pedals ---
    {
      id: "A", label: "Pedal A", type: "pedal",
      changes: [
        { string: 4, delta: +2 },   // B3  → C#4
        { string: 9, delta: +2 },   // E2  → F#2
      ],
    },
    {
      id: "B", label: "Pedal B", type: "pedal",
      changes: [
        { string: 2, delta: +1 },   // G#4 → A4
        { string: 5, delta: +1 },   // G#3 → A3
      ],
    },
    {
      id: "C", label: "Pedal C", type: "pedal",
      changes: [
        { string: 3, delta: +2 },   // E4  → F#4
        { string: 7, delta: +1 },   // D3  → D#3
      ],
    },

    // --- Knee levers ---
    {
      id: "LKL", label: "Left Knee Left", type: "lever",
      changes: [
        { string: 3, delta: -1 },  // E4  → Eb4
        { string: 7, delta: -1 },  // D3  → Db3
      ],
    },
    {
      id: "LKR", label: "Left Knee Right", type: "lever",
      changes: [
        { string: 1, delta: +1 },  // D#5 → E5
        { string: 4, delta: +1 },  // B3  → C4
      ],
    },
    {
      id: "RKL", label: "Right Knee Left", type: "lever",
      changes: [
        { string: 2, delta: -1 },  // G#4 → G4
        { string: 5, delta: -1 },  // G#3 → G3
      ],
    },
    {
      id: "RKR", label: "Right Knee Right", type: "lever",
      changes: [
        { string: 0, delta: +1 },  // F#5 → G5
      ],
    },
  ],
};

// =============================================================================
// PRESET: C6 Universal (6-string lap-steel / sacred-steel style)
//
//  String:  0    1    2    3    4    5
//  Open:    A4   F4   C4   A3   F3   C3
// =============================================================================

export const C6_UNIVERSAL: Copedant = {
  id: "c6-universal",
  name: "C6 Universal (6-string)",

  openTuning: ["A4", "F4", "C4", "A3", "F3", "C3"],

  controls: [
    {
      id: "P1", label: "Pedal 1", type: "pedal",
      changes: [
        { string: 2, delta: +2 },  // C4 → D4
        { string: 5, delta: +2 },  // C3 → D3
      ],
    },
    {
      id: "P2", label: "Pedal 2", type: "pedal",
      changes: [
        { string: 0, delta: -2 },  // A4 → G4
        { string: 3, delta: -2 },  // A3 → G3
      ],
    },
    {
      id: "LK", label: "Left Knee", type: "lever",
      changes: [
        { string: 1, delta: +1 },  // F4 → F#4
        { string: 4, delta: +1 },  // F3 → F#3
      ],
    },
  ],
};

// =============================================================================
// CSV Import Utility
// =============================================================================

/**
 * Example CSV template for defining a copedant.
 *
 * Format:
 *   TUNING row:    "TUNING,<note0>,<note1>,..."
 *   CONTROL rows:  "CONTROL,<id>,<label>,<type>,<string0_delta>,<string1_delta>,..."
 *                  Use 0 for strings the control doesn't affect.
 *
 * The number of columns in CONTROL rows must match the number of strings.
 */
export const CSV_TEMPLATE = `# Sacred Steel Engine - Copedant CSV Template
# Lines starting with # are comments and are ignored.
# TUNING row: comma-separated open-string note names (string 0 first).
TUNING,F#5,D#5,G#4,E4,B3,G#3,E3,D3,B2,E2
# CONTROL rows: id, label, type (pedal|lever), then one delta per string.
# Use 0 for strings not affected by the control.
CONTROL,A,Pedal A,pedal,0,0,0,0,+2,0,0,0,0,+2
CONTROL,B,Pedal B,pedal,0,0,+1,0,0,+1,0,0,0,0
CONTROL,C,Pedal C,pedal,0,0,0,+2,0,0,0,+1,0,0
CONTROL,LKL,Left Knee Left,lever,0,0,0,-1,0,0,0,-1,0,0
CONTROL,LKR,Left Knee Right,lever,0,+1,0,0,+1,0,0,0,0,0
CONTROL,RKL,Right Knee Left,lever,0,0,-1,0,0,-1,0,0,0,0
CONTROL,RKR,Right Knee Right,lever,+1,0,0,0,0,0,0,0,0,0
`.trim();

/**
 * Parse a copedant CSV string into a Copedant object.
 *
 * Throws on malformed input.
 *
 * @param csv     Raw CSV text (see CSV_TEMPLATE for format).
 * @param id      ID to assign to the resulting copedant.
 * @param name    Name to assign to the resulting copedant.
 */
export function parseCopedantCSV(
  csv: string,
  id: string = "csv-copedant",
  name: string = "Imported Copedant"
): Copedant {
  const lines = csv
    .split(/\r?\n/)
    .map(l => l.trim())
    .filter(l => l.length > 0 && !l.startsWith("#"));

  let openTuning: string[] = [];
  const controls: Copedant["controls"] = [];

  for (const line of lines) {
    const cols = line.split(",").map(c => c.trim());
    const rowType = cols[0].toUpperCase();

    if (rowType === "TUNING") {
      openTuning = cols.slice(1);
      // Validate each note is parseable
      for (const note of openTuning) {
        parseNoteToMidi(note); // throws on bad note name
      }
    } else if (rowType === "CONTROL") {
      if (cols.length < 5) {
        throw new Error(`parseCopedantCSV: CONTROL row too short: "${line}"`);
      }
      const [, cId, cLabel, cTypeRaw, ...deltaStrs] = cols;
      const cType = cTypeRaw.toLowerCase() as "pedal" | "lever";
      if (cType !== "pedal" && cType !== "lever") {
        throw new Error(`parseCopedantCSV: unknown control type "${cTypeRaw}"`);
      }

      const changes = deltaStrs
        .map((d, idx) => ({ string: idx, delta: parseInt(d, 10) || 0 }))
        .filter(c => c.delta !== 0);  // omit strings this control doesn't touch

      controls.push({ id: cId, label: cLabel, type: cType, changes });
    } else {
      throw new Error(`parseCopedantCSV: unknown row type "${cols[0]}"`);
    }
  }

  if (openTuning.length === 0) {
    throw new Error("parseCopedantCSV: no TUNING row found");
  }

  return { id, name, openTuning, controls };
}
